import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { FiMapPin, FiNavigation, FiClock, FiAlertTriangle } from "react-icons/fi";
import { StatusBadge, statusColor, healthColor } from "./fleetHelpers";

// Fix Leaflet default icon path issue in webpack builds
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const VEHICLE_TYPE_COLOR = {
  "Heavy Truck": "#f97316",
  "Medium Truck": "#3b82f6",
  "Light Truck":  "#10b981",
  "Cargo Van":    "#8b5cf6",
  "EV Truck":     "#06b6d4",
  "EV Van":       "#06b6d4",
};

const VEHICLE_LETTER = {
  "Heavy Truck":  "H",
  "Medium Truck": "M",
  "Light Truck":  "L",
  "Cargo Van":    "V",
  "EV Truck":     "E",
  "EV Van":       "E",
};

const STATUS_OPACITY = {
  "Active":      1,
  "Idle":        0.75,
  "Maintenance": 0.6,
  "Offline":     0.45,
};

/** Build a custom Leaflet divIcon for a vehicle pin */
function makeVehicleIcon(vehicle, isSelected) {
  const col = VEHICLE_TYPE_COLOR[vehicle.type] || "#667eea";
  const letter = VEHICLE_LETTER[vehicle.type] || "T";
  const size = isSelected ? 38 : 30;
  const borderColor = isSelected ? "#1e293b" : "white";
  const borderWidth = isSelected ? 3 : 2;
  const opacity = STATUS_OPACITY[vehicle.status] ?? 1;
  const pulse = vehicle.status === "Active" && !isSelected
    ? `box-shadow: 0 0 0 4px ${col}33, 0 2px 8px rgba(0,0,0,0.3);`
    : "box-shadow: 0 2px 6px rgba(0,0,0,0.3);";

  const alertDot = vehicle.alerts.length > 0
    ? `<div style="position:absolute;top:-2px;right:-2px;width:10px;height:10px;
         background:#ef4444;border-radius:50%;border:1.5px solid white;"></div>`
    : "";

  return L.divIcon({
    className: "",
    html: `
      <div style="
        position: relative;
        width:${size}px; height:${size}px;
        background:${col};
        border:${borderWidth}px solid ${borderColor};
        border-radius:50%;
        display:flex; align-items:center; justify-content:center;
        color:white; font-size:${Math.round(size * 0.42)}px; font-weight:700;
        opacity:${opacity};
        ${pulse}
        transition: all 0.2s;
        cursor: pointer;
      ">${letter}${alertDot}</div>`,
    iconSize:   [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2 + 4)],
  });
}

/** Helper component: flies map to the selected vehicle when it changes */
function FlyToVehicle({ vehicle }) {
  const map = useMap();
  useEffect(() => {
    if (vehicle?.lat && vehicle?.lng) {
      map.flyTo([vehicle.lat, vehicle.lng], 12, { duration: 1.2 });
    }
  }, [vehicle, map]);
  return null;
}

// ── Main Component ────────────────────────────────────────────────────────────
const TrackingPanel = ({ d }) => {
  const [selected, setSelected]   = useState(null);
  const [filter,   setFilter]     = useState("All");
  const listRef = useRef(null);

  const statuses = ["All", "Active", "Idle", "Maintenance", "Offline"];

  const filtered = filter === "All"
    ? d.vehicles
    : d.vehicles.filter(v => v.status === filter);

  const selectedVehicle = selected
    ? d.vehicles.find(v => v.id === selected)
    : null;

  /** When a vehicle is selected, scroll its row into view in the list */
  useEffect(() => {
    if (selected && listRef.current) {
      const el = listRef.current.querySelector(`[data-vid="${selected}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected]);

  const handleSelectVehicle = (id) => {
    setSelected(prev => (prev === id ? null : id));
  };

  return (
    <div className="fleet-tracking">

      {/* ── Filter Bar ── */}
      <div className="ft-filter-bar">
        {statuses.map(s => (
          <button
            key={s}
            className={`ft-filter-chip ${filter === s ? "ft-chip-active" : ""}`}
            onClick={() => setFilter(s)}
          >
            {s !== "All" && (
              <span className="ft-chip-dot" style={{ background: statusColor(s) }} />
            )}
            {s}
            <span className="ft-chip-count">
              {s === "All"
                ? d.vehicles.length
                : d.vehicles.filter(v => v.status === s).length}
            </span>
          </button>
        ))}
      </div>

      {/* ── Main Layout: List + Map ── */}
      <div className="ft-layout">

        {/* Vehicle List */}
        <div className="ft-vehicle-list" ref={listRef}>
          {filtered.map(v => {
            const col = VEHICLE_TYPE_COLOR[v.type] || "#667eea";
            const isActive = selected === v.id;
            return (
              <div
                key={v.id}
                data-vid={v.id}
                className={`ft-vehicle-item ${isActive ? "ft-vehicle-active" : ""}`}
                onClick={() => handleSelectVehicle(v.id)}
              >
                <div className="ft-veh-dot" style={{ background: col, borderColor: isActive ? "#1e293b" : col }}>
                  {VEHICLE_LETTER[v.type] || "T"}
                </div>
                <div className="ft-veh-info">
                  <div className="ft-veh-name">
                    {v.name}
                    <span className="ft-veh-id">{v.id}</span>
                  </div>
                  <div className="ft-veh-driver">{v.driver}</div>
                  <div className="ft-veh-meta">
                    <StatusBadge status={v.status} />
                    {v.route !== "—" && (
                      <span className="ft-veh-route">
                        <FiNavigation size={11} /> {v.route}
                      </span>
                    )}
                  </div>
                </div>
                {v.alerts.length > 0 && (
                  <div className="ft-veh-alert-dot">
                    <FiAlertTriangle size={13} color="#ef4444" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Map + Detail */}
        <div className="ft-map-side">

          {/* Leaflet Map Card */}
          <div className="ft-map-card" style={{ padding: 0, overflow: "hidden" }}>
            <div className="ft-map-title" style={{ padding: "10px 14px", marginBottom: 0 }}>
              <FiMapPin size={14} /> Maharashtra Fleet – Live Map
              <span style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8" }}>
                OpenStreetMap
              </span>
            </div>

            <MapContainer
              center={[19.0, 73.8]}
              zoom={7}
              style={{ width: "100%", height: 400 }}
              scrollWheelZoom={true}
              zoomControl={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Fly-to effect when selecting a vehicle */}
              {selectedVehicle && <FlyToVehicle vehicle={selectedVehicle} />}

              {/* Vehicle Markers */}
              {d.vehicles.map(v => {
                if (!v.lat || !v.lng) return null;
                const showOnMap = filter === "All" || v.status === filter;
                if (!showOnMap) return null;

                const isSelected = selected === v.id;
                return (
                  <Marker
                    key={v.id}
                    position={[v.lat, v.lng]}
                    icon={makeVehicleIcon(v, isSelected)}
                    eventHandlers={{
                      click: () => handleSelectVehicle(v.id),
                    }}
                    zIndexOffset={isSelected ? 1000 : (v.status === "Active" ? 500 : 0)}
                  >
                    <Popup>
                      <div style={{ minWidth: 160, fontSize: 12 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                          {v.name} <span style={{ color: "#94a3b8", fontWeight: 400 }}>({v.id})</span>
                        </div>
                        <div style={{ color: "#64748b", marginBottom: 2 }}>{v.type}</div>
                        <div style={{ marginBottom: 2 }}>📍 {v.location}</div>
                        {v.route !== "—" && <div style={{ marginBottom: 2 }}>🛣 {v.route}</div>}
                        <div style={{ marginBottom: 2 }}>👤 {v.driver}</div>
                        <div>🏎 {v.speed} km/h &nbsp;|&nbsp; ⛽ {v.isEV ? `${v.battery}% bat` : `${v.fuel}%`}</div>
                        {v.alerts.length > 0 && (
                          <div style={{ marginTop: 6, color: "#ef4444", fontWeight: 600 }}>
                            ⚠ {v.alerts[0]}
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>

            {/* Legend */}
            <div className="ft-map-legend" style={{ padding: "8px 14px" }}>
              {Object.entries(VEHICLE_TYPE_COLOR).map(([type, col]) => (
                <span key={type} className="ft-legend-item">
                  <span className="ft-legend-dot" style={{ background: col }} />
                  {type}
                </span>
              ))}
            </div>
          </div>

          {/* Selected Vehicle Detail */}
          {selectedVehicle && (
            <div className="ft-detail-card">
              <div className="ft-detail-header">
                <div>
                  <div className="ft-detail-name">{selectedVehicle.name}</div>
                  <div className="ft-detail-sub">
                    {selectedVehicle.plateNo} · {selectedVehicle.type}
                  </div>
                </div>
                <StatusBadge status={selectedVehicle.status} />
              </div>

              <div className="ft-detail-grid">
                <div className="ft-detail-item">
                  <span className="ft-di-label">Driver</span>
                  <span className="ft-di-value">{selectedVehicle.driver}</span>
                </div>
                <div className="ft-detail-item">
                  <span className="ft-di-label">Driver Score</span>
                  <span className="ft-di-value" style={{ color: healthColor(selectedVehicle.driverScore || 0) }}>
                    {selectedVehicle.driverScore ?? "—"}
                  </span>
                </div>
                <div className="ft-detail-item">
                  <span className="ft-di-label">Location</span>
                  <span className="ft-di-value">{selectedVehicle.location}</span>
                </div>
                <div className="ft-detail-item">
                  <span className="ft-di-label">Coordinates</span>
                  <span className="ft-di-value" style={{ fontSize: 11, color: "#94a3b8" }}>
                    {selectedVehicle.lat?.toFixed(4)}, {selectedVehicle.lng?.toFixed(4)}
                  </span>
                </div>
                <div className="ft-detail-item">
                  <span className="ft-di-label">Route</span>
                  <span className="ft-di-value">{selectedVehicle.route}</span>
                </div>
                {selectedVehicle.eta !== "—" && (
                  <div className="ft-detail-item">
                    <span className="ft-di-label"><FiClock size={11} /> ETA</span>
                    <span className="ft-di-value">{selectedVehicle.eta}</span>
                  </div>
                )}
                <div className="ft-detail-item">
                  <span className="ft-di-label">Speed</span>
                  <span className="ft-di-value">{selectedVehicle.speed} km/h</span>
                </div>
                <div className="ft-detail-item">
                  <span className="ft-di-label">Fuel / Battery</span>
                  <span className="ft-di-value">
                    {selectedVehicle.isEV
                      ? `${selectedVehicle.battery}% battery`
                      : `${selectedVehicle.fuel}%`}
                  </span>
                </div>
                <div className="ft-detail-item">
                  <span className="ft-di-label">Health Score</span>
                  <span className="ft-di-value" style={{ color: healthColor(selectedVehicle.healthScore) }}>
                    {selectedVehicle.healthScore}%
                  </span>
                </div>
                <div className="ft-detail-item">
                  <span className="ft-di-label">Failure Risk</span>
                  <span className="ft-di-value" style={{ color: selectedVehicle.failureRisk > 60 ? "#ef4444" : selectedVehicle.failureRisk > 30 ? "#f59e0b" : "#10b981" }}>
                    {selectedVehicle.failureRisk}%
                  </span>
                </div>
              </div>

              {selectedVehicle.alerts.length > 0 && (
                <div className="ft-detail-alerts">
                  {selectedVehicle.alerts.map((a, i) => (
                    <div key={i} className="ft-da-row">
                      <FiAlertTriangle size={12} color="#ef4444" />
                      {a}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackingPanel;
